import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Check, Droplets, ShieldCheck, Waves, Wrench, FlameKindling } from 'lucide-react';
import { useCollections } from '@/hooks/useCollections';
import { FINISHES } from '@/data/catalogue';

const SPECS = [
  { icon: Waves, label: 'Perfect Water Flow' },
  { icon: FlameKindling, label: 'Hot & Cold Suitable' },
  { icon: ShieldCheck, label: 'Quality Control Approved' },
  { icon: Wrench, label: 'Low Maintenance' },
  { icon: Droplets, label: 'Operates Smoothly' },
];

export const Configurator = () => {
  const collections = useCollections();
  const navigate = useNavigate();
  const models = useMemo(
    () => collections.filter((c) => ['sapphire', 'platinum', 'pluto', 'iris', 'victor', 'freesia'].includes(c.id)),
    [collections]
  );
  const [modelId, setModelId] = useState('sapphire');
  const [finishId, setFinishId] = useState('chrome');

  const model = models.find((m) => m.id === modelId) || models[0];
  const finish = FINISHES.find((f) => f.id === finishId);

  return (
    <section
      id="configurator"
      data-testid="product-configurator"
      className="relative py-28 sm:py-36 bg-[#0D0D0F] border-y border-[#D4AF37]/10 overflow-hidden"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 55% 60% at 25% 50%, rgba(212,175,55,0.06), transparent 65%)' }}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="mb-14">
          <p className="kicker mb-4">Interactive — The Finish Atelier</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light max-w-2xl">
            Choose a silhouette.<br />
            <span className="italic text-gold-gradient">Dress it in metal.</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="relative">
            <div className="absolute -inset-3 border border-[#D4AF37]/20 pointer-events-none" />
            <div className="relative aspect-[4/5] sm:aspect-square overflow-hidden bg-[#121215]">
              <AnimatePresence mode="wait">
                <motion.img
                  key={`${model?.id}-${finishId}`}
                  src={model?.image}
                  alt={`${model?.name} in ${finish?.name}`}
                  data-testid="configurator-preview"
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full h-full object-cover"
                  style={{ filter: finish.filter === 'none' ? undefined : finish.filter }}
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0F]/90 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{finish?.name}</p>
                  <p className="font-serif text-2xl">{model?.name}</p>
                </div>
                {model?.featured && (
                  <p className="font-mono text-xs text-zinc-400" data-testid="configurator-model-code">
                    {model.featured.model}
                  </p>
                )}
              </div>
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
              Finish previews are indicative — request physical swatches via enquiry.
            </p>
          </div>

          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4">01 — Silhouette</p>
            <div className="grid grid-cols-3 gap-2 mb-10" data-testid="configurator-models">
              {models.map((m) => (
                <button
                  key={m.id}
                  data-testid={`configurator-model-${m.id}`}
                  onClick={() => setModelId(m.id)}
                  className={`px-3 py-3 border font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                    modelId === m.id
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#F5E6AD]'
                      : 'border-zinc-800 text-zinc-400 hover:border-[#D4AF37]/50 hover:text-zinc-200'
                  }`}
                >
                  {m.name}
                </button>
              ))}
            </div>

            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4">02 — Engineering</p>
            <div className="flex flex-wrap gap-2 mb-10" data-testid="configurator-specs">
              {SPECS.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="flex items-center gap-2 border border-zinc-800 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400"
                >
                  <Icon size={13} className="text-[#D4AF37]" /> {label}
                </span>
              ))}
            </div>

            {model?.featured && (
              <div className="border border-[#D4AF37]/20 bg-[#121215] p-5 mb-8" data-testid="configurator-featured">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Featured piece</p>
                <div className="flex items-center justify-between mt-2 gap-4">
                  <p className="text-sm text-zinc-200">
                    <span className="font-mono text-xs text-[#D4AF37] mr-2">{model.featured.model}</span>
                    {model.featured.name}
                  </p>
                  <button
                    data-testid="configurator-inquire-btn"
                    onClick={() => navigate('/contact', { state: { prefill: { collection: model.name, model: model.featured.model, product: model.featured.name, finish: finish?.name } } })}
                    className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] hover:text-[#F5E6AD] transition-colors whitespace-nowrap"
                  >
                    Inquire Now
                  </button>
                </div>
              </div>
            )}

            <button
              data-testid="configurator-enquire-btn"
              onClick={() => navigate('/contact', { state: { prefill: { collection: model?.name, finish: finish?.name } } })}
              className="w-full sm:w-auto bg-[#D4AF37] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] px-10 py-4 hover:bg-[#F5E6AD] transition-colors duration-300"
            >
              Enquire in {finish?.name}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
